import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  Bookmark, MessageCircle, MessageSquare, Calendar, Briefcase, Download, Sparkles,
  ArrowRight, MapPin, BookOpen, Handshake, Check, X as XIcon, Inbox, Send,
  Users, Newspaper, Edit, ExternalLink, Lock,
} from "lucide-react";
import { API, useAuth } from "@/App";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import DashboardHeader from "@/components/layout/DashboardHeader";
import PartnersStrip from "@/components/PartnersStrip";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useMatches } from "@/components/MatchContext";
import SuggestionBox from "@/components/SuggestionBox";

/**
 * Returns true when the user is "premium" — admins, founders, founding members,
 * or anyone on a paid membership tier all qualify. Free members see a teaser
 * upgrade card for premium-only tabs (Pinned / Matches / Suggest etc).
 */
const isPremiumMember = (u) =>
  !!u && (u.is_admin || u.is_founder || u.is_founding_member || (u.membership_tier && u.membership_tier !== "free"));

const Section = ({ title, count, children, empty }) => (
  <section className="mb-8">
    <div className="flex items-baseline justify-between mb-3 border-b border-[#E5E5E5] pb-2">
      <h3 className="text-lg font-display font-bold text-[#0A0A0A]">{title}</h3>
      {typeof count === "number" && (
        <span className="text-xs text-[#4A4A4A]">{count} {count === 1 ? "item" : "items"}</span>
      )}
    </div>
    {count === 0 ? <p className="text-sm text-[#4A4A4A] italic py-4">{empty}</p> : children}
  </section>
);

const memberPicture = (m) => {
  if (!m?.picture) return null;
  return m.picture.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${m.picture}` : m.picture;
};

const Avatar = ({ member, size = 56, ring = "#FF2D87" }) => (
  <div
    className="rounded-full overflow-hidden border-2 flex-shrink-0 bg-[#FF2D87] flex items-center justify-center text-white font-bold"
    style={{ width: size, height: size, borderColor: ring }}
  >
    {memberPicture(member)
      ? <img src={memberPicture(member)} alt={member.name} className="w-full h-full object-cover" />
      : (member.name?.[0]?.toUpperCase() || "?")}
  </div>
);

const MemberRow = ({ member, action, accent = "#FF2D87", testId }) => (
  <div className="bg-[#0A0A0A] text-white p-4 flex items-center gap-4" data-testid={testId}>
    <Link to={`/profile/${member.user_id}`} className="flex items-center gap-3 flex-1 min-w-0 hover:opacity-80 transition-opacity">
      <Avatar member={member} ring={accent} />
      <div className="min-w-0">
        <p className="font-bold truncate" style={{ color: accent }}>{member.name}</p>
        <p className="text-xs text-white/70 truncate">
          {[member.title, member.company].filter(Boolean).join(" @ ") || "—"}
        </p>
        {member.goals?.length > 0 && (
          <p className="text-[10px] text-white/50 truncate mt-0.5">
            {member.goals.slice(0, 2).join(" · ")}
          </p>
        )}
      </div>
    </Link>
    <div className="flex items-center gap-2 flex-shrink-0">{action}</div>
  </div>
);

const formatDate = (d) => new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
const timeAgo = (dateString) => {
  if (!dateString) return "";
  const t = new Date(dateString).getTime();
  const mins = Math.floor((Date.now() - t) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(dateString).toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

const Dashboard = () => {
  const { user } = useAuth();
  const matches = useMatches();
  const premium = isPremiumMember(user);
  const [pinned, setPinned] = useState({ members: [], businesses: [], posts: [], events: [] });
  const [myEvents, setMyEvents] = useState([]);
  const [myDiscussions, setMyDiscussions] = useState([]);
  const [myReplies, setMyReplies] = useState([]);
  const [myBusiness, setMyBusiness] = useState(null);
  const [whatsNew, setWhatsNew] = useState(null);
  const [activeTab, setActiveTab] = useState("feed");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/pins`).catch(() => ({ data: { members: [], businesses: [], posts: [], events: [] } })),
      axios.get(`${API}/users/me/events`).catch(() => ({ data: [] })),
      axios.get(`${API}/users/me/discussions`).catch(() => ({ data: [] })),
      axios.get(`${API}/users/me/replies`).catch(() => ({ data: [] })),
      axios.get(`${API}/users/me/business`).catch(() => ({ data: null })),
      axios.get(`${API}/users/me/whats-new`).catch(() => ({ data: null })),
    ]).then(([pinsRes, evtRes, discRes, replyRes, bizRes, whatsNewRes]) => {
      setPinned(pinsRes.data);
      setMyEvents(evtRes.data || []);
      setMyDiscussions(discRes.data || []);
      setMyReplies(replyRes.data || []);
      setMyBusiness(bizRes.data || null);
      setWhatsNew(whatsNewRes.data || null);
    }).finally(() => setLoading(false));
  }, [user]);

  useEffect(() => { matches.refresh(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  // Build "What's new since we saw you last" feed from /api/users/me/whats-new
  const sinceFeed = useMemo(() => {
    if (!whatsNew) return [];
    const items = [];
    (whatsNew.posts || []).forEach((p) => items.push({
      kind: "post", id: p.post_id, title: p.title,
      subtitle: p.author_name && !p.hide_author ? `By ${p.author_name}` : "New article",
      href: `/blog/${p.slug}`, ts: p.created_at, icon: Newspaper, accent: "#FF2D87", label: "New post",
    }));
    (whatsNew.events || []).forEach((e) => items.push({
      kind: "event", id: e.event_id, title: e.title,
      subtitle: `${formatDate(e.date)}${e.location ? ` · ${e.location}` : ""}`,
      href: `/events`, ts: e.created_at || e.date, icon: Calendar, accent: "#5170FF", label: "Event",
    }));
    (whatsNew.businesses || []).forEach((b) => items.push({
      kind: "business", id: b.business_id, title: b.name,
      subtitle: b.tagline || b.industry || "New business",
      href: `/business/${b.business_id}`, ts: b.created_at, icon: Briefcase, accent: "#5170FF", label: "Business",
    }));
    (whatsNew.discussions || []).forEach((d) => items.push({
      kind: "discussion", id: d.discussion_id, title: d.title,
      subtitle: `${d.author_name} · ${d.replies_count} ${d.replies_count === 1 ? "reply" : "replies"}`,
      href: `/community/${d.discussion_id}`, ts: d.created_at, icon: MessageCircle, accent: "#0A0A0A", label: "Community",
    }));
    (whatsNew.replies_to_me || []).forEach((r) => items.push({
      kind: "reply", id: r.reply_id || `${r.discussion_id}-${r.created_at}`,
      title: r.thread_title || "Reply on your thread",
      subtitle: `${r.author_name || "Someone"} replied · "${(r.content || "").slice(0, 80)}${(r.content || "").length > 80 ? "…" : ""}"`,
      href: `/community/${r.discussion_id}`, ts: r.created_at, icon: MessageSquare, accent: "#FF2D87", label: "Reply to you",
    }));
    return items.filter((x) => x.ts).sort((a, b) => new Date(b.ts) - new Date(a.ts));
  }, [whatsNew]);

  const repliesToMe = (whatsNew?.replies_to_me || []).length;

  const incoming = matches.data.pending_incoming || [];
  const confirmed = matches.data.confirmed || [];
  const outgoing = matches.data.pending_outgoing || [];
  const autos = matches.data.auto_matches || [];

  // Tab definitions. `premiumOnly` tabs are hidden from free members and shown
  // with an upgrade prompt when navigated to directly.
  const ALL_TABS = [
    { value: "feed", label: "What's New", icon: Sparkles },
    { value: "profile", label: "Profile", icon: Edit },
    { value: "business", label: "My Business", icon: Briefcase },
    { value: "pinned", label: "Pinned", icon: Bookmark, premiumOnly: true },
    { value: "matches", label: "Matches", icon: Handshake, badge: matches.incomingCount, premiumOnly: true },
    { value: "events", label: "My Events", icon: Calendar, premiumOnly: true },
    { value: "discussions", label: "My Discussions", icon: MessageCircle, premiumOnly: true },
    { value: "replies", label: "My Replies", icon: MessageSquare, premiumOnly: true, badge: repliesToMe },
    { value: "downloads", label: "Downloads", icon: Download, premiumOnly: true },
    { value: "suggest", label: "Suggest", icon: Sparkles, premiumOnly: true },
  ];
  const TABS = ALL_TABS.filter((t) => premium || !t.premiumOnly);

  return (
    <div className="min-h-screen bg-white" data-testid="dashboard-page">
      <Navbar />
      <DashboardHeader />

      <div className="container-main py-10">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="flex flex-wrap gap-2 bg-transparent p-0 mb-8 border-b-2 border-[#E5E5E5] rounded-none w-full justify-start h-auto">
            {TABS.map((t) => (
              <TabsTrigger
                key={t.value}
                value={t.value}
                className="relative rounded-none border-2 border-transparent data-[state=active]:bg-[#0A0A0A] data-[state=active]:text-white data-[state=active]:border-[#0A0A0A] px-4 py-2 text-xs font-bold uppercase tracking-widest gap-2"
                data-testid={`tab-${t.value}`}
              >
                <t.icon className="w-3.5 h-3.5" /> {t.label}
                {t.badge > 0 && (
                  <span className="ml-1 bg-[#FF2D87] text-white rounded-full text-[10px] px-1.5 min-w-[18px] h-[18px] flex items-center justify-center font-bold" data-testid={`tab-badge-${t.value}`}>
                    {t.badge}
                  </span>
                )}
              </TabsTrigger>
            ))}
          </TabsList>

          {loading ? <div className="skeleton h-40" /> : (
            <>
              {/* What's new since we saw you last */}
              <TabsContent value="feed">
                <div className="mb-5">
                  <h3 className="text-2xl md:text-3xl font-display font-bold text-[#0A0A0A] mb-1">
                    What's new since we saw you last
                    {user?.name && <span className="text-[#FF2D87]">, {user.name.split(" ")[0]}</span>}
                  </h3>
                  <p className="text-sm text-[#4A4A4A]">
                    {whatsNew?.is_first_visit
                      ? "First time here? Below is everything from the past two weeks across the ensemble."
                      : <>Updates since your last visit{whatsNew?.since ? ` on ${new Date(whatsNew.since).toLocaleDateString("en-US", { month: "short", day: "numeric" })}` : ""}.</>}
                    {repliesToMe > 0 && (
                      <span className="ml-2 inline-flex items-center gap-1 bg-[#5170FF] text-white text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded-full">
                        <MessageSquare className="w-3 h-3" /> {repliesToMe} new {repliesToMe === 1 ? "reply" : "replies"} to you
                      </span>
                    )}
                  </p>
                </div>
                {sinceFeed.length === 0 ? (
                  <div className="bg-white border-2 border-[#E5E5E5] p-10 text-center">
                    <Sparkles className="w-8 h-8 text-[#FFC2E8] mx-auto mb-3" />
                    <p className="font-display text-xl font-bold mb-1">All caught up.</p>
                    <p className="text-sm text-[#4A4A4A]">Nothing new since we last saw you — come back soon.</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {sinceFeed.map((it) => (
                      <Link
                        key={`${it.kind}-${it.id}`}
                        to={it.href}
                        className="group bg-white border-2 border-[#0A0A0A] p-4 hover:bg-[#FFC2E8] transition-colors flex items-start gap-3"
                        data-testid={`feed-${it.kind}-${it.id}`}
                      >
                        <div
                          className="w-12 h-12 flex-shrink-0 flex items-center justify-center text-white border-2 border-[#0A0A0A]"
                          style={{ background: it.accent }}
                        >
                          {it.avatar
                            ? <img src={it.avatar.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${it.avatar}` : it.avatar} alt="" className="w-full h-full object-cover" />
                            : <it.icon className="w-5 h-5" style={{ color: it.accent === "#FFC2E8" ? "#0A0A0A" : "white" }} />
                          }
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-[10px] font-bold uppercase tracking-widest" style={{ color: it.accent === "#FFC2E8" ? "#FF2D87" : it.accent }}>
                            {it.label} · {timeAgo(it.ts)}
                          </p>
                          <p className="font-bold text-[#0A0A0A] truncate">{it.title}</p>
                          <p className="text-xs text-[#4A4A4A] truncate">{it.subtitle}</p>
                        </div>
                      </Link>
                    ))}
                  </div>
                )}

                {!premium && (
                  <div className="mt-10 bg-[#FFC2E8] border-2 border-[#0A0A0A] p-6 md:p-8 shadow-[6px_6px_0_#0A0A0A]" data-testid="upgrade-suggest">
                    <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
                      <div className="w-14 h-14 bg-[#0A0A0A] border-2 border-[#0A0A0A] flex items-center justify-center flex-shrink-0">
                        <Lock className="w-6 h-6 text-[#FF2D87]" />
                      </div>
                      <div className="flex-1">
                        <p className="overline text-[#FF2D87] mb-1">Premium</p>
                        <h3 className="text-xl md:text-2xl font-display font-bold leading-tight">Unlock the full Portal</h3>
                        <p className="text-sm text-[#4A4A4A] mt-1">
                          Pinned items, mutual Matches, saved Events, your Discussions & Replies, and the private Suggestion Box live inside <strong>My Portal</strong> — available to Founding Members and paid tiers.
                        </p>
                      </div>
                      <a href="https://ko-fi.com/thecreativeensemble" target="_blank" rel="noopener noreferrer" className="bg-[#0A0A0A] text-white font-bold rounded-full px-5 py-2.5 hover:bg-[#FF2D87] transition-colors text-xs uppercase tracking-widest whitespace-nowrap" data-testid="upgrade-cta">
                        Become a Founding Member
                      </a>
                    </div>
                  </div>
                )}
              </TabsContent>

              {/* Pinned */}
              <TabsContent value="pinned">
                <Section title="Pinned members" count={pinned.members.length} empty="No pinned members yet. Tap the bookmark icon on a profile to save it.">
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {pinned.members.map((m) => (
                      <Link key={m.user_id} to={`/profile/${m.user_id}`} className="bg-[#0A0A0A] text-white p-4 hover:-translate-y-1 hover:shadow-xl transition-all flex items-center gap-3" data-testid={`pinned-member-${m.user_id}`}>
                        <Avatar member={m} size={48} />
                        <div className="flex-1 min-w-0">
                          <p className="font-bold text-[#FF2D87] truncate">{m.name}</p>
                          <p className="text-xs text-white/70 truncate">{[m.title, m.company].filter(Boolean).join(" @ ")}</p>
                        </div>
                      </Link>
                    ))}
                  </div>
                </Section>

                <Section title="Pinned businesses" count={pinned.businesses.length} empty="No pinned businesses yet.">
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {pinned.businesses.map((b) => (
                      <Link key={b.business_id} to={`/business/${b.business_id}`} className="bg-[#0A0A0A] text-white p-4 hover:-translate-y-1 hover:shadow-xl transition-all flex items-center gap-3" data-testid={`pinned-business-${b.business_id}`}>
                        <div className="w-12 h-12 overflow-hidden border-2 border-white flex-shrink-0 bg-[#5170FF] flex items-center justify-center text-white font-bold">
                          {b.logo_url ? <img src={b.logo_url} alt="" className="w-full h-full object-cover" /> : (b.name?.[0] || "?")}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-bold text-[#5170FF] truncate">{b.name}</p>
                          <p className="text-xs text-white/70 truncate">{b.tagline || b.industry}</p>
                        </div>
                      </Link>
                    ))}
                  </div>
                </Section>

                <Section title="Pinned posts" count={pinned.posts.length} empty="No pinned articles yet.">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {pinned.posts.map((p) => (
                      <Link key={p.post_id} to={`/blog/${p.slug}`} className="bg-white border-2 border-[#0A0A0A] p-4 hover:bg-[#FFC2E8] transition-colors flex items-center gap-3" data-testid={`pinned-post-${p.post_id}`}>
                        <div className="w-14 h-14 bg-[#FF2D87] flex items-center justify-center text-white flex-shrink-0">
                          {p.image_url ? <img src={p.image_url} alt="" className="w-full h-full object-cover" /> : <BookOpen className="w-5 h-5" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-bold text-[#0A0A0A] truncate">{p.title}</p>
                          <p className="text-xs text-[#4A4A4A] truncate">{p.excerpt}</p>
                        </div>
                      </Link>
                    ))}
                  </div>
                </Section>
              </TabsContent>

              {/* Matches */}
              <TabsContent value="matches">
                <p className="text-sm text-[#4A4A4A] mb-6 max-w-2xl">
                  <strong className="text-[#0A0A0A]">Bookmarks</strong> are private saves (find them in the Pinned tab).
                  A <strong className="text-[#5170FF]">Match</strong> is mutual — when someone sends you a 🤝 request you'll see it here, and once you accept it becomes a confirmed match for both of you.
                  <strong className="text-[#FF2D87]"> Auto-matches</strong> appear when your goals naturally complement someone else's (mentor ↔ mentee, hiring ↔ available, collab ↔ collab).
                </p>

                <Section title={<span className="inline-flex items-center gap-2"><Inbox className="w-5 h-5 text-[#5170FF]" /> Incoming requests</span>} count={incoming.length} empty="No one has sent you a match request yet.">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="matches-incoming">
                    {incoming.map((m) => (
                      <MemberRow key={m.user_id} member={m} accent="#5170FF" testId={`incoming-${m.user_id}`}
                        action={<>
                          <button onClick={() => matches.accept(m.request_id)} className="bg-[#5170FF] text-white rounded-full w-9 h-9 flex items-center justify-center hover:bg-white hover:text-[#5170FF] transition-colors" title="Accept" data-testid={`accept-${m.user_id}`}><Check className="w-4 h-4" /></button>
                          <button onClick={() => matches.decline(m.request_id)} className="bg-transparent text-white border border-white/30 rounded-full w-9 h-9 flex items-center justify-center hover:bg-white hover:text-[#0A0A0A] transition-colors" title="Decline" data-testid={`decline-${m.user_id}`}><XIcon className="w-4 h-4" /></button>
                        </>}
                      />
                    ))}
                  </div>
                </Section>

                <Section title={<span className="inline-flex items-center gap-2"><Handshake className="w-5 h-5 text-[#5170FF]" /> Confirmed matches</span>} count={confirmed.length} empty="No confirmed matches yet — send a 🤝 from someone's profile or member card.">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="matches-confirmed">
                    {confirmed.map((m) => (
                      <MemberRow key={m.user_id} member={m} accent="#5170FF" testId={`confirmed-${m.user_id}`}
                        action={<>
                          {m.contact_email && <a href={`mailto:${m.contact_email}`} className="bg-[#FF2D87] text-white text-[10px] font-bold uppercase tracking-widest rounded-full px-3 py-1.5 hover:bg-white hover:text-[#FF2D87] transition-colors" data-testid={`email-${m.user_id}`}>Email</a>}
                          <button onClick={() => matches.cancelOrUnmatch(m.user_id)} className="text-white/70 hover:text-white text-[10px] font-bold uppercase tracking-widest" title="Un-match" data-testid={`unmatch-${m.user_id}`}>Un-match</button>
                        </>}
                      />
                    ))}
                  </div>
                </Section>

                <Section title={<span className="inline-flex items-center gap-2"><Send className="w-5 h-5 text-[#FF2D87]" /> Pending sent</span>} count={outgoing.length} empty="No outgoing requests pending.">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="matches-outgoing">
                    {outgoing.map((m) => (
                      <MemberRow key={m.user_id} member={m} accent="#FFC2E8" testId={`outgoing-${m.user_id}`}
                        action={<button onClick={() => matches.cancelOrUnmatch(m.user_id)} className="text-white/70 hover:text-white text-[10px] font-bold uppercase tracking-widest" title="Cancel request" data-testid={`cancel-${m.user_id}`}>Cancel</button>}
                      />
                    ))}
                  </div>
                </Section>

                <Section title={<span className="inline-flex items-center gap-2"><Sparkles className="w-5 h-5 text-[#FF2D87]" /> Auto-suggested matches</span>} count={autos.length} empty="No auto-matches right now. Update your goals on your profile (e.g. 'Get mentorship' or 'Looking to hire collaborators') to surface suggestions.">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="matches-auto">
                    {autos.map((m) => (
                      <MemberRow key={m.user_id} member={m} accent="#FF2D87" testId={`auto-${m.user_id}`}
                        action={<>
                          <button onClick={() => matches.request(m.user_id, m.name)} className="bg-[#FF2D87] text-white text-[10px] font-bold uppercase tracking-widest rounded-full px-3 py-1.5 hover:bg-white hover:text-[#FF2D87] transition-colors inline-flex items-center gap-1" data-testid={`auto-match-${m.user_id}`}><Handshake className="w-3 h-3" /> Match</button>
                          <button onClick={() => matches.dismissAuto(m.user_id)} className="text-white/60 hover:text-white" title="Dismiss" data-testid={`auto-dismiss-${m.user_id}`}><XIcon className="w-4 h-4" /></button>
                        </>}
                      />
                    ))}
                  </div>
                </Section>
              </TabsContent>

              {/* Events */}
              <TabsContent value="events">
                <Section title="Saved events" count={pinned.events.length} empty="No saved events yet. Tap the bookmark icon on an event to save it for later.">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {pinned.events.map((e) => (
                      <Link key={e.event_id} to="/events" className="bg-[#FFC2E8] border-2 border-[#0A0A0A] p-4 hover:-translate-y-1 hover:shadow-xl transition-all flex items-center gap-3" data-testid={`saved-event-${e.event_id}`}>
                        <div className="w-14 h-14 bg-[#FF2D87] text-white flex items-center justify-center flex-shrink-0 overflow-hidden">
                          {e.image_url ? <img src={e.image_url} alt="" className="w-full h-full object-cover" /> : <Calendar className="w-5 h-5" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-bold text-[#0A0A0A] truncate">{e.title}</p>
                          <p className="text-xs text-[#0A0A0A]/70 truncate">{formatDate(e.date)} · {e.location}</p>
                        </div>
                      </Link>
                    ))}
                  </div>
                </Section>

                <Section title="Registered events" count={myEvents.length} empty="No events registered yet.">
                  <div className="space-y-3">
                    {myEvents.map((e) => (
                      <div key={e.event_id} className="bg-[#0A0A0A] text-white border-2 border-[#0A0A0A] p-4 flex items-center gap-4" data-testid={`my-event-${e.event_id}`}>
                        <div className="w-20 h-20 flex-shrink-0 overflow-hidden border-2 border-white bg-[#FF2D87] flex items-center justify-center">
                          {e.image_url ? <img src={e.image_url} alt="" className="w-full h-full object-cover" /> : <Calendar className="w-6 h-6 text-white" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-bold text-[#FF2D87]">{e.title}</p>
                          <p className="text-sm text-white/70 flex items-center gap-3 mt-1">
                            <span className="inline-flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {formatDate(e.date)}</span>
                            <span className="inline-flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {e.location}</span>
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </Section>
              </TabsContent>

              {/* Discussions */}
              <TabsContent value="discussions">
                <Section title="Discussions I've started" count={myDiscussions.length} empty="You haven't started any discussions yet.">
                  <div className="space-y-3">
                    {myDiscussions.map((d) => (
                      <Link key={d.discussion_id} to={`/community/${d.discussion_id}`} className="block bg-white border-2 border-[#0A0A0A] p-4 hover:bg-[#FFC2E8] transition-colors" data-testid={`my-discussion-${d.discussion_id}`}>
                        <p className="font-bold text-[#0A0A0A]">{d.title}</p>
                        <p className="text-xs text-[#4A4A4A] mt-1 inline-flex items-center gap-3">
                          <span>{formatDate(d.created_at)}</span>
                          <span className="inline-flex items-center gap-1"><MessageCircle className="w-3 h-3" /> {d.replies_count} replies</span>
                        </p>
                      </Link>
                    ))}
                  </div>
                </Section>
              </TabsContent>

              {/* Replies */}
              <TabsContent value="replies">
                <Section title="Discussions I've replied to" count={myReplies.length} empty="You haven't replied to any discussions yet.">
                  <div className="space-y-3">
                    {myReplies.map((d) => (
                      <Link key={d.discussion_id} to={`/community/${d.discussion_id}`} className="block bg-white border-2 border-[#0A0A0A] p-4 hover:bg-[#FFC2E8] transition-colors" data-testid={`my-reply-${d.discussion_id}`}>
                        <p className="font-bold text-[#0A0A0A]">{d.title}</p>
                        <p className="text-xs text-[#4A4A4A] mt-1">By {d.author_name} · last reply by you {formatDate(d.my_last_reply_at)}</p>
                      </Link>
                    ))}
                  </div>
                </Section>
              </TabsContent>

              {/* Profile */}
              <TabsContent value="profile">
                <Section title="Your member profile">
                  <div className="bg-[#0A0A0A] text-white p-6 md:p-8" data-testid="profile-card">
                    <div className="flex flex-col md:flex-row items-start gap-6">
                      <Avatar member={user} size={96} />
                      <div className="flex-1 min-w-0">
                        <h3 className="font-display font-bold text-2xl text-[#FF2D87]">{user?.name}</h3>
                        <p className="text-sm text-white/70 mt-1">
                          {[user?.title, user?.company].filter(Boolean).join(" @ ") || "Add a title and company so members know what you do"}
                        </p>
                        {user?.bio ? (
                          <p className="text-sm text-white/80 mt-3 line-clamp-3">{user.bio}</p>
                        ) : (
                          <p className="text-sm text-white/50 italic mt-3">No bio yet — add one to help members get to know you.</p>
                        )}
                        <div className="flex flex-wrap gap-2 mt-4">
                          <Link to="/profile" className="inline-flex items-center gap-2 bg-[#FF2D87] text-white text-xs font-bold uppercase tracking-widest rounded-full px-4 py-2 hover:bg-white hover:text-[#0A0A0A] transition-colors" data-testid="cta-edit-profile">
                            <Edit className="w-3.5 h-3.5" /> Edit profile
                          </Link>
                          <Link to={`/profile/${user?.user_id}`} className="inline-flex items-center gap-2 bg-transparent text-white border-2 border-white/30 text-xs font-bold uppercase tracking-widest rounded-full px-4 py-2 hover:bg-white hover:text-[#0A0A0A] transition-colors" data-testid="cta-view-public">
                            <ExternalLink className="w-3.5 h-3.5" /> View public profile
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                </Section>
              </TabsContent>

              {/* Business */}
              <TabsContent value="business">
                {myBusiness ? (
                  <div className="bg-[#0A0A0A] text-white p-6 max-w-2xl" data-testid="my-business-card">
                    <div className="flex items-start gap-4 mb-4">
                      {myBusiness.logo_url ? (
                        <img src={myBusiness.logo_url} alt="" className="w-20 h-20 object-cover border-2 border-white" />
                      ) : (
                        <div className="w-20 h-20 bg-[#5170FF] border-2 border-white flex items-center justify-center font-display font-bold text-3xl">
                          {myBusiness.name[0]}
                        </div>
                      )}
                      <div className="flex-1">
                        <h3 className="font-display font-bold text-2xl text-[#5170FF]">{myBusiness.name}</h3>
                        <p className="text-white/70 text-sm">{myBusiness.tagline}</p>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Link to={`/business/${myBusiness.business_id}`} className="bg-[#FF2D87] text-white font-bold rounded-full px-5 py-2.5 hover:bg-white hover:text-[#0A0A0A] transition-colors text-xs uppercase tracking-widest inline-flex items-center gap-2" data-testid="cta-edit-business">
                        <Edit className="w-3.5 h-3.5" /> Manage business
                      </Link>
                      <Link to="/businesses" className="bg-transparent text-white border-2 border-white/30 font-bold rounded-full px-5 py-2.5 hover:bg-white hover:text-[#0A0A0A] transition-colors text-xs uppercase tracking-widest inline-flex items-center gap-2">
                        View directory
                      </Link>
                    </div>
                  </div>
                ) : (
                  <div className="bg-[#FFC2E8] border-2 border-[#0A0A0A] p-8 text-center max-w-2xl">
                    <Briefcase className="w-10 h-10 text-[#FF2D87] mx-auto mb-3" />
                    <p className="font-display text-xl font-bold mb-2">You don't have a business listed yet.</p>
                    <p className="text-sm text-[#4A4A4A] mb-5">Add yours to the directory to be discovered by other members.</p>
                    <Link to="/businesses" className="bg-[#0A0A0A] text-white font-bold rounded-full px-5 py-2.5 hover:bg-[#FF2D87] transition-colors text-xs uppercase tracking-widest inline-flex items-center gap-2" data-testid="cta-add-business">
                      Add my business <ArrowRight className="w-4 h-4" />
                    </Link>
                  </div>
                )}
              </TabsContent>

              <TabsContent value="downloads">
                <Section title="Resources I've downloaded" count={0} empty="No downloads yet — when we share PDFs, guides, and worksheets, they'll show up here for easy access.">
                  <div />
                </Section>
              </TabsContent>

              <TabsContent value="suggest">
                <SuggestionBox />
              </TabsContent>
            </>
          )}
        </Tabs>
      </div>

      <PartnersStrip />
      <Footer />
    </div>
  );
};

export default Dashboard;
